import { ApiError } from '@/lib/api';
import { pinWord } from '@/lib/references';
import { versionedDay } from '@/lib/versioned-labels';
import type {
  CriterionThreshold,
  ExitAction,
  ExitActionKind,
  ExitCondition,
  ExitTrigger,
  ObjectiveClassTrigger,
  ObjectiveOutcome,
  ProtocolJump,
  ProtocolProblem,
  ProtocolProductionBlocker,
  ProtocolRetiredCriterion,
  ProtocolStepDraft,
  ProtocolUnscoredThreshold,
  SnapshotBand,
  StepRules,
  VersionTag,
} from '@matter_extractor/shared';
import { assertNever, protocolErrorSchema } from '@matter_extractor/shared';

/**
 * The words that belong to the PROTOCOLE and to nothing else (refonte modulaire
 * §7, §8.4; maquette `editeur-protocole.html`).
 *
 * Everything a screen of the versioning pattern says without knowing what it
 * is looking at is in `versioned-labels.ts`. What is here is the protocol's own
 * vocabulary, word for word the mockup's and the spec's: the triggers, the
 * actions, the sentence a condition reads as, the label of a jump on the frise,
 * the recap of a step's rules, and the sentence each refusal is worded with.
 *
 * ## One file for three readers
 *
 * The step editor writes a condition as a sentence, the frise labels a jump
 * with the head of that sentence (« si premier facteur + n ») and shows the
 * whole of it on hover, the tree labels its branches the same way. Three
 * components, one wording: a condition that read differently on the frise and
 * in the editor would be two conditions to an operator's eye.
 *
 * PURE, and every function takes the steps it needs rather than reading state:
 * `vitest.config.ts` excludes `src/routes/**` from the coverage report, so a
 * sentence assembled in a component would be a rule nothing could assert.
 *
 * The identifiers are English (`turn-cap-reached`) because a stored enum is
 * code; the sentence an operator reads is French, and it is written once, here.
 */

/* --------------------------------------------------------------- the words */

export const OBJECTIVE_CLASS_LABEL: Readonly<Record<ObjectiveClassTrigger, string>> = {
  'all-mandatory-succeeded': 'tous les obligatoires réussis',
  'all-optional-succeeded': 'tous les optionnels réussis',
  'mandatory-failed': 'un obligatoire raté',
  'optional-failed': 'un optionnel raté',
};

/** The three counters a condition can listen to (§8.4). */
export type CounterTriggerKind =
  | 'turn-cap-reached'
  | 'attempts-exhausted'
  | 'empty-answers-exceeded';

export const COUNTER_TRIGGER_LABEL: Readonly<Record<CounterTriggerKind, string>> = {
  'turn-cap-reached': 'plafond de tours atteint',
  'attempts-exhausted': 'essais épuisés',
  'empty-answers-exceeded': 'quota de réponses vides dépassé',
};

/** « devient réussi » / « devient raté » - the two destinations a trigger names (§7.3). */
export const OUTCOME_LABEL: Readonly<Record<ObjectiveOutcome, string>> = {
  succeeded: 'réussi',
  failed: 'raté',
};

/** The seven actions of §8.4, in the order the mockup's select lists them. */
export const ACTION_LABEL: Readonly<Record<ExitActionKind, string>> = {
  direction: 'direction d’IA',
  'invalidate-objective': 'invalider un objectif',
  'close-step': 'clôturer l’étape',
  'replay-step': 'relancer l’étape',
  'go-to-step': 'aller à une étape',
  'close-protocol': 'clôturer le protocole',
  'close-experience': 'fermer l’expérience',
};

export const ACTION_KINDS: readonly ExitActionKind[] = [
  'direction',
  'invalidate-objective',
  'close-step',
  'replay-step',
  'go-to-step',
  'close-protocol',
  'close-experience',
];

/** « obligatoire » / « optionnel » - the pill of an objective (§7.2). */
export function obligationLabel(mandatory: boolean): string {
  return mandatory ? 'obligatoire' : 'optionnel';
}

/** « 1 étape » / « 3 étapes » - French plural on a count, for the facts of a card. */
export function countLabel(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/* ------------------------------------------------------------- the lookups */

/** What a sentence needs to know about a step: its rank and its name. */
export type StepPlace = { readonly index: number; readonly name: string };

/** What a sentence needs to know about an objective: its name and where it lives. */
export type ObjectivePlace = {
  readonly name: string;
  readonly stepIndex: number;
  readonly mandatory: boolean;
};

export type StepLookup = (stepId: string) => StepPlace | undefined;
export type ObjectiveLookup = (objectiveId: string) => ObjectivePlace | undefined;

export function stepLookupOf(steps: readonly ProtocolStepDraft[]): StepLookup {
  const places = new Map<string, StepPlace>();
  steps.forEach((step, index) => places.set(step.id, { index, name: step.name }));
  return (stepId) => places.get(stepId);
}

export function objectiveLookupOf(steps: readonly ProtocolStepDraft[]): ObjectiveLookup {
  const places = new Map<string, ObjectivePlace>();
  steps.forEach((step, stepIndex) => {
    for (const objective of step.objectives) {
      places.set(objective.id, { name: objective.name, stepIndex, mandatory: objective.mandatory });
    }
  });
  return (objectiveId) => places.get(objectiveId);
}

/**
 * « 4 · Émergence », or « Étape 4 » while the step has no name yet: a draft's
 * step may be unnamed, and a sentence with a hole in it reads as broken.
 */
export function stepTitle(place: StepPlace): string {
  const name = place.name.trim();
  return name === '' ? `Étape ${place.index + 1}` : `${place.index + 1} · ${name}`;
}

/** « Objectif 2 » for an unnamed objective, its name otherwise. */
export function objectiveTitle(name: string, fallbackIndex: number): string {
  return name.trim() === '' ? `Objectif ${fallbackIndex + 1}` : name.trim();
}

/* ----------------------------------------------------------- the sentences */

/** « Contexte de vie raté », « tous les obligatoires réussis », « plafond de tours atteint ». */
export function triggerSentence(trigger: ExitTrigger, objectives: ObjectiveLookup): string {
  switch (trigger.kind) {
    case 'objective-status': {
      const objective = objectives(trigger.objectiveId);
      const name = objective === undefined ? 'un objectif disparu' : objective.name;
      return `${name} ${OUTCOME_LABEL[trigger.to]}`;
    }
    case 'objective-class':
      return OBJECTIVE_CLASS_LABEL[trigger.which];
    case 'turn-cap-reached':
    case 'attempts-exhausted':
    case 'empty-answers-exceeded':
      return COUNTER_TRIGGER_LABEL[trigger.kind];
    default:
      return assertNever(trigger, 'triggerSentence');
  }
}

/** How many triggers a condition carries, all groups together. */
export function factorCount(condition: Pick<ExitCondition, 'groups'>): number {
  return condition.groups.reduce((total, group) => total + group.length, 0);
}

/**
 * The frise's label of a condition (§7.1): « si <premier facteur> + n », the
 * whole sentence being the hover.
 */
export function conditionShortLabel(
  condition: Pick<ExitCondition, 'groups'>,
  objectives: ObjectiveLookup,
): string {
  const first = condition.groups[0]?.[0];
  if (first === undefined) return 'si (aucun déclencheur)';
  const count = factorCount(condition);
  return `si ${triggerSentence(first, objectives)}${count > 1 ? ` + ${count - 1}` : ''}`;
}

/**
 * The whole condition as prose: « quand A et B ou C » - « et » inside a group,
 * « ou » between groups, the form of the filters everybody knows (§8.4).
 */
export function conditionSentence(
  condition: Pick<ExitCondition, 'groups'>,
  objectives: ObjectiveLookup,
): string {
  const groups = condition.groups.map((group) =>
    group.map((trigger) => triggerSentence(trigger, objectives)).join(' et '),
  );
  return `quand ${groups.join(' ou ')}`;
}

/** What a single action reads as, its target named when it has one. */
export function actionSentence(
  action: ExitAction,
  steps: StepLookup,
  objectives: ObjectiveLookup,
): string {
  switch (action.kind) {
    case 'go-to-step': {
      const step = steps(action.stepId);
      return step === undefined ? 'aller à une étape disparue' : `aller à ${stepTitle(step)}`;
    }
    case 'invalidate-objective': {
      const objective = objectives(action.objectiveId);
      return objective === undefined
        ? 'invalider un objectif disparu'
        : `invalider ${objective.name}`;
    }
    case 'direction': {
      const text = action.text.trim();
      const shown = text.length > 60 ? `${text.slice(0, 60)}…` : text;
      return shown === '' ? 'direction d’IA (vide)' : `direction d’IA « ${shown} »`;
    }
    case 'close-step':
    case 'replay-step':
    case 'close-protocol':
    case 'close-experience':
      return ACTION_LABEL[action.kind];
    default:
      return assertNever(action, 'actionSentence');
  }
}

/** « invalider Contexte de vie, puis aller à 3 · Contexte » - the actions in a row (§8.4). */
export function actionsSentence(
  actions: readonly ExitAction[],
  steps: StepLookup,
  objectives: ObjectiveLookup,
): string {
  if (actions.length === 0) return 'aucune action';
  return actions.map((action) => actionSentence(action, steps, objectives)).join(', puis ');
}

/** The label of a jump on the frise: the condition's head, or the fallback's own words (§7.1). */
export function jumpLabel(
  jump: ProtocolJump,
  steps: readonly ProtocolStepDraft[],
  objectives: ObjectiveLookup,
): string {
  const { source } = jump;
  if (source.kind === 'fallback') return 'repli · au-delà des essais';
  const from = steps.find((step) => step.id === jump.from);
  const condition = from?.conditions.find((one) => one.id === source.conditionId);
  if (condition === undefined) return 'si (condition disparue)';
  return conditionShortLabel(condition, objectives);
}

/**
 * The recap sentence of the rules panel (§7.2), generated from the values, or
 * null while the cap is not posed - the panel then says the sentence will write
 * itself.
 */
export function rulesRecapSentence(rules: StepRules, steps: StepLookup): string | null {
  if (rules.maxTurns === null) return null;
  const turns = countLabel(rules.maxTurns, 'tour');
  const empties = `${countLabel(rules.emptyAnswersAllowed, 'réponse')} vide${rules.emptyAnswersAllowed > 1 ? 's' : ''}`;
  const replay =
    rules.attempts > 1 ? `l’étape est rejouée, ${rules.attempts} essais en tout, puis ` : '';
  const target = rules.fallback.kind === 'go-to-step' ? steps(rules.fallback.stepId) : undefined;
  const end =
    rules.fallback.kind === 'go-to-step'
      ? target === undefined
        ? 'la session va à une étape disparue.'
        : `la session va à l’étape ${stepTitle(target)}.`
      : 'l’expérience est fermée avec le texte de clôture.';
  return `Après ${turns}, ${empties} d’affilée ou le budget IA dépassé, si un objectif obligatoire manque encore : ${replay}${end}`;
}

/* ------------------------------------------------------------- the refusals */

/** The step half of a problem's sentence: « Étape 2 · Contexte ». */
function problemStep(problem: { stepIndex: number; stepName: string }): string {
  return `Étape ${stepTitle({ index: problem.stepIndex, name: problem.stepName })}`;
}

/** What each refusal of §7.1 (and H22) says, naming its step so the screen can link to it. */
export function problemSentence(problem: ProtocolProblem): string {
  switch (problem.kind) {
    case 'step-without-cap':
      return `${problemStep(problem)} n’a pas de plafond de tours`;
    case 'farewell-step-not-empty':
      // Says the way out too: the editor never writes this state, so whoever
      // meets it has a content posed by hand and needs to know it is the box.
      return `${problemStep(problem)} est une étape d’adieu mais porte encore des objectifs ou des conditions de sortie : décochez « Étape d’adieu » pour les voir`;
    case 'reread-on-farewell-step':
      // Issue #165, H93: the editor unticks the reread when the farewell is
      // ticked, so this is a content posed by hand - the sentence names the box.
      return `${problemStep(problem)} est une étape d’adieu mais relit aussi l’étape d’où l’on vient : décochez « À l’entrée, relire l’étape d’où l’on vient »`;
    case 'hide-objectives-on-farewell-step':
      // Issue #191, H19: the editor unticks the case when the farewell is
      // ticked, so this is a content posed by hand - the sentence names the box.
      return `${problemStep(problem)} est une étape d’adieu mais cache aussi ses objectifs à l’interviewer : décochez « Cacher les objectifs à l’interviewer »`;
    case 'objective-without-criterion':
      return `${objectiveTitle(problem.objectiveName, 0)} (${problemStep(problem).toLowerCase()}) n’a aucun critère`;
    case 'or-group-too-small':
      return `${objectiveTitle(problem.objectiveName, 0)} (${problemStep(problem).toLowerCase()}) a un groupe « l’un de » d’un seul critère : il en faut au moins deux`;
    case 'jump-to-missing-step':
      return `${problemStep(problem)} : la condition ${problem.conditionIndex + 1} vise une étape disparue`;
    case 'fallback-to-missing-step':
      return `${problemStep(problem)} : le repli vise une étape disparue`;
    case 'trigger-on-missing-objective':
      return `${problemStep(problem)} : la condition ${problem.conditionIndex + 1} écoute un objectif disparu`;
    case 'action-on-missing-objective':
      return `${problemStep(problem)} : la condition ${problem.conditionIndex + 1} invalide un objectif disparu`;
    // The two of the Rangement section (#177, §4) name no step.
    case 'rangement-consigne-missing':
      return 'Rangement : des objectifs cibles sont cochés, mais la consigne du rangeur est vide';
    case 'rangement-targets-missing':
      return 'Rangement : la consigne du rangeur n’a aucun objectif cible coché';
    default:
      return assertNever(problem, 'problemSentence');
  }
}

/** Where a blocker sits: « (Ce que ça a changé, étape 3) » or « (direction, étape 3) ». */
function blockerPlace(blocker: ProtocolProductionBlocker): string {
  const step = `étape ${blocker.stepIndex + 1}`;
  return blocker.objectiveName === null
    ? `(direction, ${step})`
    : `(${blocker.objectiveName}, ${step})`;
}

/** What « Passer en production » refuses, one criterion at a time (§4, §7.1). */
export function blockerSentence(blocker: ProtocolProductionBlocker): string {
  const name = `« ${blocker.criterionName} » ${blockerPlace(blocker)}`;
  switch (blocker.kind) {
    case 'criterion-without-production':
      return `${name} n’a aucune version en production`;
    case 'criterion-retired':
      return `${name} est retiré depuis le ${versionedDay(blocker.retiredAt)}`;
    case 'criterion-unknown':
      return `${name} n’existe plus`;
    case 'threshold-without-scoring':
      // The PIN is in the sentence because the answer differs by citation: a
      // criterion scored in its latest version and bare in the one a citation
      // pins is a pin to move, not a threshold to remove (§7.2).
      return `${name} porte un seuil de ${blocker.threshold} et n’a pas de notation dans la version ${pinWord(blocker.pin)} qu’il cite`;
    default:
      return assertNever(blocker, 'blockerSentence');
  }
}

/**
 * « production » / « développement » - the tag as every screen spells it (§4).
 *
 * Exported because the criterion's row writes it too, in the yellow pill of a
 * threshold with no notation: two tables would be the day one screen starts
 * saying « dev ».
 */
export const TAG_WORD: Readonly<Record<VersionTag, string>> = {
  production: 'production',
  development: 'développement',
};

/**
 * The warning line a card carries for a threshold posed on a criterion the
 * cited version does not score (§7.2, grill of 2026-09-16).
 *
 * The GESTURE first, like its retired neighbour: the card is scanned, and the
 * first words have to be what to do about it. Two gestures are possible and
 * both are named - the operator chooses, and neither is obviously right.
 *
 * A null `objectiveName` is worded as one instead of leaving « l'objectif
 * «  » » on the card: the place shares `blockerPlace` with a warning that can
 * come from a direction, so the hole is reachable by the type even where only
 * an objective poses a threshold today.
 */
export function unscoredThresholdSentence(entry: ProtocolUnscoredThreshold): string {
  const who =
    entry.objectiveName === null ? 'un objectif sans nom' : `l’objectif « ${entry.objectiveName} »`;
  return `Seuil sans notation : le critère « ${entry.criterionName} » n’est pas noté dans la version ${pinWord(entry.pin)} qu’il cite, et ${who} (étape ${entry.stepIndex + 1}) lui pose un seuil de ${entry.threshold}. Retirer le seuil, ou noter le critère.`;
}

/**
 * The warning line a card carries for a criterion it cites and that has been
 * retired (§4, §7.1, §15).
 *
 * It says the GESTURE first - « Remplacer le critère X » - and the place after,
 * because the card is scanned and the first three words have to be what to do
 * about it. §4 fixes the wording of the warning itself (« remplacer le critère
 * X, retiré le ... »), and the citation site is what this screen adds so an
 * operator knows which of nine steps to open.
 *
 * A null `objectiveName` is a `{{critere:id}}` variable in a direction rather
 * than an objective's criterion (`protocolRetiredCriterionSchema`), and it is
 * worded as one instead of leaving a hole where a name would be.
 */
export function retiredCriterionSentence(entry: ProtocolRetiredCriterion): string {
  const where =
    entry.objectiveName === null
      ? `cité par une direction (étape ${entry.stepIndex + 1})`
      : `cité par l’objectif « ${entry.objectiveName} » (étape ${entry.stepIndex + 1})`;
  return `Remplacer le critère « ${entry.criterionName} », retiré le ${versionedDay(entry.retiredAt)}, ${where}.`;
}

/** « 3 problèmes » - the head's yellow pill (§7.1). */
export function problemCountLabel(count: number): string {
  return countLabel(count, 'problème');
}

/* ------------------------------------------------------------ the threshold */

/**
 * The BAR a score was measured against (§7.2, §8.2): the threshold the
 * objective posed, or, with none, the plancher of the criterion's own notation.
 *
 * Here and not beside one of its readers, because there are FOUR - the citation
 * row of the step editor, the Objectifs layer, the État column and the played
 * protocol drawer. One sentence, one place.
 *
 * The two are named APART and never folded into one word: « seuil 75 » is a
 * decision of this objective, « plancher 50 » is a fact about the criterion, and
 * an operator reading the second has nothing to change here. H18 and its
 * « 50 par défaut » are retired with the grill of 2026-09-16 (issue #79): there
 * is no longer any number the dispositif invents.
 *
 * `null` on a criterion with neither, which is the unscored one: it is judged on
 * a verdict, and there is no bar to print beside it - « pas de seuil » would be
 * an answer to a question nobody asked of it (§7.2: « pour un critère non noté,
 * rien, pas même verdict »).
 *
 * No operator and no unit: a threshold is a minimum on the 0 to 100 a score
 * lives on, so « seuil >= 80 » printed the comparison twice and a `%` would have
 * been a percentage of nothing.
 */
export function thresholdLabel(
  threshold: CriterionThreshold | null,
  plancher: number | null,
): string | null {
  if (threshold !== null) return `seuil ${threshold}`;
  if (plancher !== null) return `plancher ${plancher}`;
  return null;
}

/**
 * « passable dès 25 » (§12.3, issue #110): where the frozen scale says a score
 * that missed its bar is still passable - null when it marks no such band, so
 * a scale written before #110 prints exactly what it printed.
 */
export function passableLabel(passable: number | null): string | null {
  return passable === null ? null : `passable dès ${passable}`;
}

/** « rempli » / « non rempli » - what an UNSCORED criterion reads as (§8.2, §12.3). */
export function verdictLabel(verdict: boolean | null): string {
  if (verdict === null) return 'pas encore jugé';
  return verdict ? 'rempli' : 'non rempli';
}

/**
 * « 62 · tranche 50-75 » (§8.2, §12.3): the score, and the band the CODE found
 * it in.
 *
 * « Il ne nomme pas la tranche, il rend le nombre, et le code retrouve la
 * tranche à partir du nombre pour l'afficher. » The band travels frozen on the
 * snapshot (§8.7), so this only prints it - a reader that resolved it again
 * would show today's scale beside a score measured on another one.
 */
export function scoreLabel(score: number | null, band: SnapshotBand | null): string {
  if (score === null) return '-';
  if (band === null) return String(score);
  return `${score} · tranche ${band.lower}-${band.upper}`;
}

/**
 * H47, the garde au gel (§8.2), as the reader of §12.3 says it.
 *
 * A sentence and not a pill, because it explains why a number on the screen
 * decided nothing - and the operator's next gesture is in the protocol editor,
 * where the same fact is a yellow pill on the row.
 */
export const IGNORED_THRESHOLD_SENTENCE = 'seuil ignoré : critère non noté dans la version gelée';

/**
 * The two 409s that belong to the protocol, worded from their `code` (§7.1,
 * issue #49), or `null` when the failure carried neither.
 *
 * The twin of `versionedRefusalOf` + `versionedRefusal`, folded into one
 * function because there are two codes and they are already sentences: the
 * catalogue is `protocolErrorSchema`, deliberately apart from the six of
 * `versionedErrorSchema` because these are states of the CONTENT and not of the
 * base. `versioned-editor` tries this one FIRST (its `ownRefusal` slot) and
 * falls back to the six, so a `name-taken` on a protocol still reads as a
 * `name-taken`.
 *
 * `null` on anything else - a 500, a network cut, a body of another shape - so
 * the caller falls back to its generic sentence, which is the honest answer when
 * nothing typed came back. The parse is a `safeParse` for the same reason:
 * `ApiError.details` is kept unparsed by `lib/api.ts` on purpose (see its head),
 * and a body that is not one of the two reads as « no typed refusal » rather
 * than crashing the screen.
 *
 * Both sentences name what is wrong RATHER than counting it alone: the box that
 * refused already lists the problems, but this sentence also lands under a
 * « Passer en production » fired from the history, where nothing else does.
 */
export function protocolRefusalSentence(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const parsed = protocolErrorSchema.safeParse(error.details);
  if (!parsed.success) return null;
  const refusal = parsed.data;
  switch (refusal.code) {
    case 'invalid-steps': {
      const sentences = refusal.problems.map(problemSentence).join(' ; ');
      const count = problemCountLabel(refusal.problems.length);
      return sentences === ''
        ? `L’enregistrement est refusé : ${count}.`
        : `L’enregistrement est refusé : ${count}. ${sentences}`;
    }
    case 'production-unresolved': {
      const sentences = refusal.blockers.map(blockerSentence).join(' ; ');
      return `Refusé en production : ${sentences}`;
    }
    default:
      return assertNever(refusal.code, 'protocolRefusalSentence');
  }
}
